/**
 * PKCE (Proof Key for Code Exchange) Utilities
 * Implements OAuth 2.0 PKCE flow as per RFC 7636
 */

/**
 * Generate a random code verifier (43-128 characters)
 * @returns {string} Base64url-encoded code verifier
 */
export const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

/**
 * Generate code challenge from code verifier
 * @param {string} verifier - The code verifier
 * @returns {Promise<string>} Base64url-encoded code challenge
 */
export const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

/**
 * Base64 URL encode
 * @param {Uint8Array} array - The array to encode
 * @returns {string} Base64url-encoded string
 */
const base64UrlEncode = (array) => {
  let binaryString = '';
  for (let i = 0; i < array.byteLength; i++) {
    binaryString += String.fromCharCode(array[i]);
  }
  return btoa(binaryString)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};


/**
 * Generate authorization URL
 * @param {object} config - OAuth configuration
 * @returns {object} { authUrl, codeVerifier, state }
 */
export const generateAuthorizationUrl = async (config) => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateCodeVerifier(); // Use as state parameter
  
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: config.scope || 'openid profile email',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state: state,
    application_id: config.applicationId,
  });
  
  const authUrl = `${config.authorizationEndpoint}?${params.toString()}`;
  
  return {
    authUrl,
    codeVerifier,
    state,
    codeChallenge,
  };
};
